import { TILE } from '../core/constants';
import { Cell } from './grid';
import { astar, defaultPassable } from './pathfind';
/**
 * Класичний Bomberman-рух ворога: переміщення плавне в пікселях, але
 * напрямок змінюється тільки коли ворог візуально центрований у клітинці.
 * Це гарантує, що AI завжди оперує цілими клітинками й не «застрягає» між ними.
 */
export class Enemy {
    x;
    y;
    r;
    speed;
    kind;
    dir; // напрямок руху, (0,0) = стоп
    pathRecalcTicks; // лічильник до перерахунку A*
    cachedPath;
    prevDir; // для Onil — щоб не повертатися назад
    constructor(col, row, kind) {
        this.x = col * TILE + TILE / 2;
        this.y = row * TILE + TILE / 2;
        this.r = TILE * 0.36;
        this.kind = kind;
        this.speed = kind === 'pass' ? 1.4 : kind === 'onil' ? 1.2 : 1.0;
        this.dir = { dx: 0, dy: 0 };
        this.prevDir = { dx: 0, dy: 0 };
        this.pathRecalcTicks = 0;
        this.cachedPath = [];
    }
    /** Чи ворог приблизно центрований у клітинці (поріг ≤ 1px). */
    isCentered() {
        const cx = ((this.x % TILE) + TILE) % TILE;
        const cy = ((this.y % TILE) + TILE) % TILE;
        return Math.abs(cx - TILE / 2) < 1.0 && Math.abs(cy - TILE / 2) < 1.0;
    }
    cell() {
        return { c: Math.floor(this.x / TILE), r: Math.floor(this.y / TILE) };
    }
    passableDirs(grid, bombs) {
        const { c, r } = this.cell();
        const result = [];
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nc = c + dx, nr = r + dy;
            if (grid.get(nc, nr) !== Cell.Floor)
                continue;
            if (bombs.some(b => b.col === nc && b.row === nr))
                continue;
            result.push({ dx, dy });
        }
        return result;
    }
    /** AI-крок (вибір напрямку), викликається тільки коли ворог центрований. */
    chooseDir(grid, bombs, playerCell, rand) {
        const passable = this.passableDirs(grid, bombs);
        if (passable.length === 0)
            return { dx: 0, dy: 0 };
        if (this.kind === 'balloom') {
            // Випадковий поворот, дозволяємо повертатися назад
            // АЛЕ: якщо можна продовжити поточний напрямок, ходимо випадково
            // (це створює класичну «броунівську» поведінку)
            return passable[Math.floor(rand() * passable.length)];
        }
        if (this.kind === 'onil') {
            // Не повертатися назад, якщо є інші опції
            const back = { dx: -this.prevDir.dx, dy: -this.prevDir.dy };
            const noBack = passable.filter(d => !(d.dx === back.dx && d.dy === back.dy));
            const pool = noBack.length > 0 ? noBack : passable;
            return pool[Math.floor(rand() * pool.length)];
        }
        // 'pass' — A* до гравця
        if (this.cachedPath.length < 2 || this.pathRecalcTicks <= 0) {
            const me = this.cell();
            this.cachedPath = astar(me, playerCell, grid, defaultPassable);
            this.pathRecalcTicks = 30; // перераховувати раз на півсекунди
        }
        if (this.cachedPath.length >= 2) {
            const me = this.cell();
            const next = this.cachedPath[1];
            // якщо перша клітинка шляху не дорівнює нашій (зрушились), скидаємо й беремо найкращий passable
            if (this.cachedPath[0].c !== me.c || this.cachedPath[0].r !== me.r) {
                this.cachedPath = [];
                return passable[Math.floor(rand() * passable.length)];
            }
            // прибрати першу — після кроку вона стане поточною
            const dx = Math.sign(next.c - me.c);
            const dy = Math.sign(next.r - me.r);
            this.cachedPath.shift();
            const dir = { dx, dy };
            // якщо напрямок не прохідний (бомбу поставили) — fallback
            if (!passable.some(p => p.dx === dx && p.dy === dy)) {
                return passable[Math.floor(rand() * passable.length)];
            }
            return dir;
        }
        return passable[Math.floor(rand() * passable.length)];
    }
    tick(grid, bombs, playerCell, rand) {
        if (this.pathRecalcTicks > 0)
            this.pathRecalcTicks--;
        if (this.isCentered()) {
            // Можливо змінити напрямок
            if (this.dir.dx === 0 && this.dir.dy === 0) {
                const d = this.chooseDir(grid, bombs, playerCell, rand);
                this.dir = d;
                this.prevDir = d;
            }
            else {
                // Перевіримо, чи поточний напрямок все ще прохідний
                const c = this.cell();
                const nc = c.c + this.dir.dx, nr = c.r + this.dir.dy;
                const blocked = grid.get(nc, nr) !== Cell.Floor
                    || bombs.some(b => b.col === nc && b.row === nr);
                // На роздоріжжі вирішуємо AI; також якщо заблоковано
                const onIntersection = this.passableDirs(grid, bombs).length > 2 || blocked;
                // Pass перераховує шлях частіше — на кожному центруванні
                if (this.kind === 'pass' || onIntersection) {
                    const d = this.chooseDir(grid, bombs, playerCell, rand);
                    this.dir = d;
                    this.prevDir = d;
                }
            }
            // Snap до центру клітинки (щоб не накопичувалась похибка)
            const c = this.cell();
            this.x = c.c * TILE + TILE / 2;
            this.y = c.r * TILE + TILE / 2;
        }
        this.x += this.dir.dx * this.speed;
        this.y += this.dir.dy * this.speed;
    }
}
/** Колізія ворога й гравця за хітбоксами (кругами). */
export function overlapsCircle(ax, ay, ar, bx, by, br) {
    const dx = ax - bx, dy = ay - by;
    return dx * dx + dy * dy < (ar + br) * (ar + br);
}
